import React from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';

import {
  getPropsConsideringNavigationItemState,
  NAVIGATION_ITEM_STATE,
} from './helpers/navigation-item';

import NavigationItemBackArrow from './navigation-item-back-arrow';
import NavigationItemLabel from './navigation-item-label';
import NavigationItemImage from './navigation-item-image';

import Analytics from '@app/modules/analytics';
import Router from '@app/modules/router';

const NavigationItem = ({
  isActive,
  preventSelfClose,
  path,
  onClick,
  children,
  ...props
}) => {
  const {
    shouldBrowserHandle,
    trackEvent,
    withBackButton,
    hasChannelName,
    name,
    image,
    ...restProps
  } = getPropsConsideringNavigationItemState(
    props,
    isActive ? NAVIGATION_ITEM_STATE.ACTIVE : NAVIGATION_ITEM_STATE.DEFAULT
  );

  /**
   * It can not be `new URL(path, window.location)`. In this case constructing
   * url while being on `http://example.com/path?query`, the query part will be
   * lost.
   * ```js
   * const url = new URL('/path', 'http://example.com/path?query');
   * url.toString() === 'http://example.com/path' // true
   * ```
   * This also applies for fragment part.
   *
   * Checking for explicitly defined queryParams and fragment is also important.
   * The story of a user can be like
   * / -> /pro?trk=WT201908_Upgrade -> /about
   * Moving from /pro to /about should reset any part of the url which is not
   * explicitly defined. Otherwise it may end up with something like
   * /pro?trk=WT201908_Upgrade#fragment -> /about?trk=WT201908_Upgrade#fragment
   *
   * To sum it up:
   * Query params must be explicitly defined otherwise it is considered that
   * there are no query params.
   * To keep any query params which are already in the current location, define
   * the value as something falsy, e.g. `null`, `undefined`, etc.
   * Fragment part follows the same rules.
   * */
  const url = new URL(window.location);
  url.pathname = path;
  if (Object.prototype.hasOwnProperty.call(restProps, 'queryParams')) {
    if (restProps.queryParams) {
      Object.entries(restProps.queryParams).forEach((entry) =>
        url.searchParams.set(...entry)
      );
    }
  } else {
    url.search = '';
  }
  if (Object.prototype.hasOwnProperty.call(restProps, 'fragment')) {
    if (restProps.fragment) {
      url.hash = restProps.fragment;
    }
  } else {
    url.hash = '';
  }

  const onLinkClick = (event) => {
    if (shouldBrowserHandle) {
      return;
    }

    event.preventDefault();

    if (isActive) {
      if (!preventSelfClose) {
        setTimeout(Router.navigateTo, 0, Router.getLastRoute('transfer', '/'));
      }
    } else if (trackEvent.category && trackEvent.action) {
      Analytics.trackStructEvent({
        category: trackEvent.category,
        action: trackEvent.action,
        label: trackEvent.label, //optional
      });
    }

    onClick();
  };

  return (
    <React.Fragment>
      <a href={url} onClick={onLinkClick} className="nav__link">
        {withBackButton && <NavigationItemBackArrow />}
        <NavigationItemLabel hasChannelName={hasChannelName}>
          {name}
        </NavigationItemLabel>
        <NavigationItemImage url={image} />
      </a>
      {children}
    </React.Fragment>
  );
};

NavigationItem.propTypes = {
  isActive: PropTypes.bool.isRequired,
  preventSelfClose: PropTypes.bool,
  path: PropTypes.string,
  queryParams: PropTypes.objectOf(PropTypes.string),
  fragment: PropTypes.string,
  shouldBrowserHandle: PropTypes.bool,
  hasChannelName: PropTypes.bool,
  name: PropTypes.string.isRequired,
  children: PropTypes.node,
  withBackButton: PropTypes.bool,
  image: PropTypes.string,
  trackEvent: PropTypes.shape({
    category: PropTypes.string,
    action: PropTypes.string,
    label: PropTypes.string,
  }),

  onClick: PropTypes.func,
};

NavigationItem.defaultProps = {
  trackEvent: {},

  onClick: noop,
};

export default React.memo(NavigationItem);
